import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import locale from 'date-fns/locale/es'
import cx from 'classnames'
import styles from './GroupEventsModal.module.scss'

const fmtShortDate = (d) => {
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yy = String(d.getFullYear()).slice(2)
  return `${dd}/${mm}/${yy}`
}

const formatEventDate = (day, time) => {
  try {
    const d = new Date(`${day}T12:00`)
    const weekday = format(d, 'EEEE', { locale })
    const capitalized = weekday.charAt(0).toUpperCase() + weekday.slice(1)
    return `${capitalized} · ${d.toLocaleDateString()} · ${time}`
  } catch {
    return `${day} · ${time}`
  }
}

const GroupEventsModal = ({
  show,
  events = [],
  currentEventId,
  loading,
  onClose,
  onCancelEvent,
  onCancelFuture,
  onRescheduleEvent,
  onFetchSlots,
}) => {
  const { t } = useTranslation()
  // Bug real (auditoría 2026-08-16): calculado a nivel de módulo (una sola
  // vez al importar), quedaba stale para siempre en una pestaña que cruza
  // la medianoche — la clasificación pasado/futuro de un evento nunca se
  // actualizaba sin recargar la página. Se recalcula por render.
  const today = new Date().toLocaleDateString('fr-ca')
  const [expandedId, setExpandedId] = useState(null)
  const [reschedulingId, setReschedulingId] = useState(null)
  const [rescheduleDay, setRescheduleDay] = useState('')
  const [rescheduleTime, setRescheduleTime] = useState('')
  const [availableSlots, setAvailableSlots] = useState({})
  const [slotsLoading, setSlotsLoading] = useState(false)

  useEffect(() => {
    if (!show) {
      setExpandedId(null)
      setReschedulingId(null)
      setRescheduleDay('')
      setRescheduleTime('')
      setAvailableSlots({})
    }
  }, [show])

  useEffect(() => {
    if (!rescheduleDay || !onFetchSlots) {
      setAvailableSlots({})
      return
    }
    // Bug real (auditoría 2026-08-14): sin el guard `cancelled`, cambiar de
    // día rápido (ej. click en día A y enseguida en día B) dispara 2
    // fetches superpuestos — si el de A responde DESPUÉS que el de B (nada
    // garantiza el orden de red), su .then pisaba availableSlots con los
    // horarios de A aunque rescheduleDay ya sea B.
    let cancelled = false
    setSlotsLoading(true)
    onFetchSlots(rescheduleDay)
      .then(result => { if (!cancelled) setAvailableSlots(result?.slots?.[rescheduleDay] ?? {}) })
      .catch(() => { if (!cancelled) setAvailableSlots({}) })
      .finally(() => { if (!cancelled) setSlotsLoading(false) })
    return () => { cancelled = true }
  }, [rescheduleDay])

  if (!show) return null

  const toggleExpand = (id) => {
    setExpandedId(prev => prev === id ? null : id)
    setReschedulingId(null)
  }

  const startReschedule = (e) => {
    setReschedulingId(e.id)
    setRescheduleDay(e.day)
    setRescheduleTime(e.time)
    setAvailableSlots({})
  }

  const cancelReschedule = () => {
    setReschedulingId(null)
    setRescheduleDay('')
    setRescheduleTime('')
    setAvailableSlots({})
  }

  const confirmReschedule = (e) => {
    if (!rescheduleDay || !rescheduleTime) return
    onRescheduleEvent?.(e, { day: rescheduleDay, time: rescheduleTime })
    cancelReschedule()
  }

  const modal = (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.dialog} role="dialog" aria-modal="true">
        <div className={styles.card} onClick={e => e.stopPropagation()}>

          <div className={styles.header}>
            <h3 className={styles.title}>{t('edit_sessions', 'Editar sesiones')}</h3>
            <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t('close', 'Cerrar')}>
              <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
            </button>
          </div>

          <div className={styles.sessionList}>
            {[...events].sort((a, b) => a.day.localeCompare(b.day) || a.time.localeCompare(b.time)).map(e => {
              const isPast = e.day < today
              const isCurrent = e.id === currentEventId
              const isCancelled = e.status === 'cancelled'
              const isExpanded = expandedId === e.id
              const isExpandable = !isPast && !isCancelled
              const isRescheduling = reschedulingId === e.id

              let icon
              if (isCancelled) icon = 'cancel'
              else if (isPast) icon = 'check_circle'
              else if (isCurrent) icon = 'radio_button_checked'
              else icon = 'radio_button_unchecked'

              const daySlots = Object.entries(availableSlots).filter(([, s]) => s.capacity > 0)

              return (
                <div key={e.id} className={cx(styles.sessionItem, {
                  [styles.sessionItemCancelled]: isCancelled,
                  [styles.sessionItemExpanded]: isExpanded,
                })}>
                  <div
                    className={styles.sessionItemHeader}
                    onClick={() => isExpandable && toggleExpand(e.id)}
                    style={{ cursor: isExpandable ? 'pointer' : 'default' }}
                  >
                    <div className={styles.sessionItemLeft}>
                      <span className={cx('material-symbols-outlined', styles.sessionIcon, {
                        [styles.sessionIconCurrent]:   isCurrent && !isCancelled,
                        [styles.sessionIconPast]:      isPast && !isCancelled,
                        [styles.sessionIconCancelled]: isCancelled,
                      })}>
                        {icon}
                      </span>
                      <p className={styles.sessionDate}>{formatEventDate(e.day, e.time)}</p>
                    </div>
                    {isCancelled && (
                      <p className={styles.sessionCancelledChip}>{t('cancelled_single', 'Cancelado')}</p>
                    )}
                    {isExpandable && (
                      <span className={cx('material-symbols-outlined', styles.chevron)}>
                        {isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                      </span>
                    )}
                  </div>

                  {isExpanded && !isRescheduling && !isCancelled && (
                    <div className={styles.sessionActions}>
                      <button
                        type="button"
                        className={styles.btnReschedule}
                        onClick={() => startReschedule(e)}
                      >
                        {t('reschedule_event', 'Reprogramar turno')}
                      </button>
                      <button
                        type="button"
                        className={styles.btnCancelEvent}
                        disabled={loading}
                        onClick={() => onCancelEvent?.(e)}
                      >
                        {t('cancel_event', 'Cancelar turno')}
                      </button>
                    </div>
                  )}

                  {isExpanded && isRescheduling && !isCancelled && (
                    <div className={styles.rescheduleForm}>
                      <div className={styles.rescheduleInputs}>
                        <input
                          type="date"
                          value={rescheduleDay}
                          onChange={ev => setRescheduleDay(ev.target.value)}
                          className={styles.rescheduleInput}
                          autoFocus
                        />
                        <input
                          type="time"
                          step="300"
                          value={rescheduleTime}
                          onChange={ev => setRescheduleTime(ev.target.value)}
                          className={styles.rescheduleInput}
                        />
                      </div>

                      {slotsLoading && (
                        <div className={styles.slotsLoading}>
                          <span className="spinner-border spinner-border-sm" role="status" />
                        </div>
                      )}

                      {!slotsLoading && daySlots.length > 0 && (
                        <div className={styles.slotsList}>
                          {daySlots.sort(([a], [b]) => a.localeCompare(b)).map(([time]) => (
                            <button
                              key={time}
                              type="button"
                              onClick={() => setRescheduleTime(time)}
                              className={cx(styles.slotBtn, {
                                [styles.slotBtnActive]: rescheduleTime === time,
                              })}
                            >
                              {time}
                            </button>
                          ))}
                        </div>
                      )}

                      {!slotsLoading && rescheduleDay && daySlots.length === 0 && (
                        <p className={styles.noSlots}>{t('no_times_for_day', 'Sin horarios disponibles para este día')}</p>
                      )}

                      <div className={styles.rescheduleActions}>
                        <button
                          type="button"
                          className={styles.btnCancelReschedule}
                          onClick={cancelReschedule}
                        >
                          {t('cancel', 'Cancelar')}
                        </button>
                        <button
                          type="button"
                          className={styles.btnConfirm}
                          disabled={!rescheduleDay || !rescheduleTime || loading}
                          onClick={() => confirmReschedule(e)}
                        >
                          {t('confirm', 'Confirmar')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <div className={styles.footer}>
            {!events.every(e => e.status === 'cancelled') && <button
              type="button"
              className={styles.btnCancelFuture}
              disabled={loading}
              onClick={onCancelFuture}
            >
              {loading && <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />}
              {t('cancel_upcoming_sessions', 'Cancelar próximas sesiones')}
            </button>}
          </div>

        </div>
      </div>
    </>
  )

  return createPortal(modal, document.body)
}

export default GroupEventsModal
